import React, { useState, useCallback, useEffect } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useLoginWithPasscodeMutation } from "../app/services/meseeknaApiV2"
import LogoFull from "../components/logo-full"

const TeamSimulationLogin: React.FC = () => {
  const navigate = useNavigate()
  const { teamSimulationId } = useParams<{ teamSimulationId: string }>()

  const [searchParams, setSearchParams] = useSearchParams()

  // Invites prefill the form, but never submit without the participant.
  useEffect(() => {
    if (searchParams.has("email") || searchParams.has("passcode")) {
      const remaining = new URLSearchParams(searchParams)
      remaining.delete("email")
      remaining.delete("passcode")
      setSearchParams(remaining, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Form state
  const [email, setEmail] = useState(() => searchParams.get("email") || "")
  const [passcode, setPasscode] = useState(() => searchParams.get("passcode") || "")
  const [displayName, setDisplayName] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // API mutations
  const [loginWithPasscode] = useLoginWithPasscodeMutation()

  // Email validation
  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(email.trim())
  }

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {}

    if (!email.trim()) {
      newErrors.email = "Please enter your email address"
    } else if (!isValidEmail(email)) {
      newErrors.email = "Please enter a valid email address"
    }

    if (!passcode.trim()) {
      newErrors.passcode = "Please enter your passcode"
    }

    if (!displayName.trim()) {
      newErrors.displayName = "Please enter your display name"
    } else if (displayName.trim().length < 2) {
      newErrors.displayName = "Display name must be at least 2 characters"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [email, passcode, displayName])

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      const loginData = {
        email: email.trim(),
        passcode: passcode.trim(),
        teamSimulationId: teamSimulationId!
      }

      const result = await loginWithPasscode(loginData).unwrap()

      // Store authentication data
      // Use sessionStorage for per-tab isolation so multiple team sim users
      // can log in from different tabs without overwriting each other's identity
      if (result.token) {
        sessionStorage.setItem("token", result.token)
        sessionStorage.setItem("teamSimulationId", teamSimulationId!)
        // Store user ID and name for collaboration features
        if (result.userData?.id) {
          sessionStorage.setItem("teamSimUserId", result.userData.id.toString())
          sessionStorage.setItem("userId", result.userData.id.toString())
        }

        // Parse display name into first/last name for collaboration
        const nameParts = displayName.trim().split(' ')
        const firstName = nameParts[0] || ''
        const lastName = nameParts.slice(1).join(' ') || ''

        // Store user name data for collaboration display
        // Prefer user-entered display name over API response
        sessionStorage.setItem("firstName", firstName || result.userData?.firstName || '')
        sessionStorage.setItem("lastName", lastName || result.userData?.lastName || '')
        // Store combined userName for easy access
        sessionStorage.setItem("userName", displayName.trim())
      }

      // Navigate to the collaborative simulation
      navigate(`/team-simulations/${teamSimulationId}/simulation`)
      
    } catch (error: unknown) {
      console.error("Login failed:", error)
      const errorMessage = (error as { data?: { message?: string } })?.data?.message || "Invalid email or passcode. Please try again."
      setErrors({
        submit: errorMessage
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [validateForm, email, passcode, teamSimulationId, loginWithPasscode, navigate, displayName])

  // Handle input changes and clear errors
  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: "" }))
    }
  }, [errors.email])

  const handlePasscodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPasscode(e.target.value)
    if (errors.passcode) {
      setErrors(prev => ({ ...prev, passcode: "" }))
    }
  }, [errors.passcode])

  const handleDisplayNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayName(e.target.value)
    if (errors.displayName) {
      setErrors(prev => ({ ...prev, displayName: "" }))
    }
  }, [errors.displayName])

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-col pt-[100px] px-8 pb-12 box-border items-center justify-start font-text-md-regular">
      
      {/* Logo */}
      <div className="mb-8">
        <LogoFull />
      </div>

      {/* Main Content */}
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-8">
          
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Join Team Simulation</h1>
            <p className="text-gray-600 text-sm">
              Enter your email and the passcode from your invitation to access your team simulation.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Display Name Field */}
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-1">
                Your Name <span className="text-gray-400 text-xs">(as shown to other participants)</span>
              </label>
              <input
                type="text"
                id="displayName"
                value={displayName}
                onChange={handleDisplayNameChange}
                placeholder="e.g., John Smith"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.displayName ? "border-red-500" : "border-gray-300"
                }`}
                disabled={isSubmitting}
              />
              {errors.displayName && (
                <p className="text-red-600 text-sm mt-1">{errors.displayName}</p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="your.email@company.com"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
                disabled={isSubmitting}
              />
              {errors.email && (
                <p className="text-red-600 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* Passcode Field */}
            <div>
              <label htmlFor="passcode" className="block text-sm font-medium text-gray-700 mb-1">
                Passcode
              </label>
              <input
                type="text"
                id="passcode"
                value={passcode}
                onChange={handlePasscodeChange}
                placeholder="Enter your 6-digit passcode"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.passcode ? "border-red-500" : "border-gray-300"
                }`}
                maxLength={6}
                disabled={isSubmitting}
              />
              {errors.passcode && (
                <p className="text-red-600 text-sm mt-1">{errors.passcode}</p>
              )}
            </div>

            {/* Submit Errors */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-red-600 text-sm">{errors.submit}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? "Signing In..." : "Join Simulation"}
            </button>
          </form>

          {/* Help Text */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Don&apos;t have a passcode? Check your email for the invitation or contact your administrator.
            </p>
          </div>
        </div>

        {/* Additional Help */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Having trouble? Contact{" "}
            <a href="mailto:support@meseekna.com" className="text-blue-600 hover:text-blue-800">
              support@meseekna.com
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default TeamSimulationLogin 